import { useCallback, useState } from "react";
import { toast } from "@/lib/toast";
import { useQueryClient } from "@tanstack/react-query";

import { apiPost } from "@/lib/api/apiClient";
import { captureEvent } from "@/lib/analyticsQueue";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFeatureRequestDialog } from "@/contexts/FeatureRequestDialogContext";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import {
  FEATURE_REQUEST_DESCRIPTION_MAX,
  FEATURE_REQUEST_DESCRIPTION_MIN,
  FEATURE_REQUEST_PHONE_MAX,
  FEATURE_REQUEST_TITLE_MAX,
  FEATURE_REQUEST_TITLE_MIN,
  type FeatureRequestCategory,
  type SubmitFeatureRequestRequest,
  type SubmitFeatureRequestResponse,
} from "@shared/schemas/featureRequest";

/**
 * Form state + submit handler for the FeatureRequestDialog.
 *
 * Manual useState (matching the AuthModalSignupForm convention in this
 * codebase) — RHF+Zod would add a dependency for what is otherwise five
 * simple fields. Validation is two min-length checks reflecting the same
 * limits the server enforces.
 */
export function useFeatureRequestForm() {
  const { t } = useLanguage();
  const { source, close } = useFeatureRequestDialog();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<FeatureRequestCategory>("new_flow");
  const [phone, setPhone] = useState("");
  const [notifyOnShip, setNotifyOnShip] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("new_flow");
    setPhone("");
    setNotifyOnShip(true);
    setIsSubmitting(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    // Validate on click rather than disabling the submit button — the
    // disabled state hides the cause from the user. Each invalid field
    // surfaces a specific, localized toast.
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (trimmedTitle.length < FEATURE_REQUEST_TITLE_MIN) {
      toast.error(
        t.featureRequest.validation.titleTooShort.replace(
          "{min}",
          String(FEATURE_REQUEST_TITLE_MIN),
        ),
      );
      return;
    }
    if (trimmedDescription.length < FEATURE_REQUEST_DESCRIPTION_MIN) {
      toast.error(
        t.featureRequest.validation.descriptionTooShort.replace(
          "{min}",
          String(FEATURE_REQUEST_DESCRIPTION_MIN),
        ),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedPhone = phone.trim();
      const body: SubmitFeatureRequestRequest = {
        title: trimmedTitle,
        description: trimmedDescription,
        category,
        source,
        notifyOnShip,
        // Optional — only send the field when the user actually typed a number.
        ...(trimmedPhone.length > 0 ? { phone: trimmedPhone } : {}),
      };
      const result = await apiPost<SubmitFeatureRequestResponse>(
        "/api/feature-requests",
        body,
      );

      captureEvent(ANALYTICS_EVENTS.FEATURE_REQUEST_SUBMITTED, {
        category,
        source,
        credits_granted: result.creditsGranted,
      });

      if (result.creditsGranted > 0) {
        toast.success(
          t.featureRequest.submitSuccessWithCredits.replace(
            "{count}",
            String(result.creditsGranted),
          ),
        );
      } else {
        toast.success(t.featureRequest.submitSuccess);
      }

      // Refresh "Your submitted ideas" on the Profile card if mounted.
      void queryClient.invalidateQueries({ queryKey: ["feature-requests", "mine"] });

      reset();
      close();
    } catch (err) {
      const message = err instanceof Error ? err.message : t.featureRequest.submitError;
      toast.error(message || t.featureRequest.submitError);
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    title,
    description,
    category,
    phone,
    source,
    notifyOnShip,
    t,
    queryClient,
    reset,
    close,
  ]);

  return {
    title,
    setTitle,
    description,
    setDescription,
    category,
    setCategory,
    phone,
    setPhone,
    notifyOnShip,
    setNotifyOnShip,
    isSubmitting,
    handleSubmit,
    reset,
    titleMax: FEATURE_REQUEST_TITLE_MAX,
    descriptionMax: FEATURE_REQUEST_DESCRIPTION_MAX,
    descriptionMin: FEATURE_REQUEST_DESCRIPTION_MIN,
    phoneMax: FEATURE_REQUEST_PHONE_MAX,
  };
}
